import type { Translate } from "@similarweb/i18n-provider";
import type {
    ActivationContractAlertsContentFiltersAlertsContentFilterItem,
    UserdataActivationAlertsContentFiltersDetailData,
} from "@similarweb/pro-api-client";

import type { ContentFilterDraft, FilterTarget, FilterValidationError, ValidationResult } from "../types";

export const DEFAULT_FILTER_TARGET: FilterTarget = 0;

const MAX_KEYWORDS_PER_LIST = 40;
const KEYWORDS_SEPARATOR = ",";

const parseKeywords = (raw: string): string[] => {
    if (!raw) {
        return [];
    }

    return raw
        .split(KEYWORDS_SEPARATOR)
        .map((keyword) => keyword.trim())
        .filter((keyword) => keyword.length > 0);
};

const formatKeywords = (keywords: string[]): string => keywords.join(`${KEYWORDS_SEPARATOR} `);

type ValidateFiltersParams = {
    drafts: ContentFilterDraft[];
    translate: Translate;
};

export const validateFilters = ({ drafts, translate }: ValidateFiltersParams): ValidationResult => {
    const errors: Record<string, FilterValidationError> = {};
    const seenDomains = new Set<string>();

    drafts.forEach((draft) => {
        const error: FilterValidationError = {};

        if (!draft.domain) {
            error.domain = translate("activation.settings.news.validation.domain.required");
        } else if (seenDomains.has(draft.domain)) {
            error.domain = translate("activation.settings.news.validation.domain.duplicate");
        } else {
            seenDomains.add(draft.domain);
        }

        const positiveCount = parseKeywords(draft.positiveKeywords).length;
        const negativeCount = parseKeywords(draft.negativeKeywords).length;

        if (positiveCount === 0 && negativeCount === 0) {
            error.keywords = translate("activation.settings.news.validation.keywords.some");
        }

        if (positiveCount > MAX_KEYWORDS_PER_LIST) {
            error.positiveKeywords = translate("activation.settings.news.validation.keywords.max", {
                maxCount: MAX_KEYWORDS_PER_LIST,
            });
        }

        if (negativeCount > MAX_KEYWORDS_PER_LIST) {
            error.negativeKeywords = translate("activation.settings.news.validation.keywords.max", {
                maxCount: MAX_KEYWORDS_PER_LIST,
            });
        }

        if (Object.keys(error).length > 0) {
            errors[draft.id] = error;
        }
    });

    return {
        isValid: Object.keys(errors).length === 0,
        errors,
    };
};

export const draftsToFilters = (
    drafts: ContentFilterDraft[],
): ActivationContractAlertsContentFiltersAlertsContentFilterItem[] => {
    return drafts.map((draft) => ({
        domain: draft.domain,
        filterTarget: draft.filterTarget,
        positiveKeywords: parseKeywords(draft.positiveKeywords),
        negativeKeywords: parseKeywords(draft.negativeKeywords),
    }));
};

export const filtersToDrafts = (
    filters: UserdataActivationAlertsContentFiltersDetailData,
    idFactory: () => string,
): ContentFilterDraft[] => {
    return filters.map((filter) => ({
        id: idFactory(),
        domain: filter.domain,
        filterTarget: filter.filterTarget ?? DEFAULT_FILTER_TARGET,
        positiveKeywords: formatKeywords(filter.positiveKeywords),
        negativeKeywords: formatKeywords(filter.negativeKeywords),
    }));
};
